<template>
  <v-card data-testid="tool_anaglyph_chart">
    <v-card-title style="padding: 0px;">
      <v-toolbar grey flat>
        <v-btn icon @click="close" data-testid="tool_anaglyph_chart_close">
          <v-icon>close</v-icon>
        </v-btn>
        <v-toolbar-title>{{ $t("common.anaglyph_charts") }}</v-toolbar-title>
      </v-toolbar>
    </v-card-title>
    <v-card-text>
      <v-layout align-center justify-space-between>
        <v-flex xs7>
          <v-select
            v-model="chars"
            :items="chars_options"
            :label="$t('tool_hart_chart.characters')"
          />
        </v-flex>

        <v-flex xs4>
          <v-select v-if="chars == 'both' || chars == 'letters' || chars == 'lowerboth'" v-model="alphabet" :label="$t('common.alphabet')" :items="alphabet_options" />
        </v-flex>
      </v-layout>

      <v-layout align-center justify-space-between>
        <v-flex xs7>
          <v-select
            v-model="hue_strategy"
            :label="$t('common.colour')"
            :items="hue_strategy_options"
          />
        </v-flex>

        <v-flex xs4>
          <v-select v-model="size" :label="$t('common.size')" :items="size_options" />
        </v-flex>
      </v-layout>

      <v-layout align-center justify-space-between>
        <v-flex xs7>
          <v-select
            v-if="alphabet == 'latin' && (chars == 'both' || chars == 'letters' || chars == 'lowerboth')"
            v-model="font"
            :label="$t('common.font')"
            :items="fonts_options"
          />
        </v-flex>

        <v-flex xs4>
        </v-flex>
      </v-layout>

      <v-checkbox
        v-if="hue_strategy == 'RandomRed' || hue_strategy == 'RandomGreen' || hue_strategy == 'RandomBlue'"
        v-model="fixation_lock"
        :label="$t('tool_anaglyph_chart.fixation_lock')"
        :hint="$t('tool_anaglyph_chart.fixation_lock_hint')"
        persistent-hint
      />

      <v-sheet style="margin-top:20px">
        <p>For best results, CMYK printers should use the following colour settings:</p>
        <table>
          <tr>
            <td>Red</td>
            <td>C=0 / M=100 / Y=100 / K=0</td>
          </tr>
          <tr>
            <td>Green</td>
            <td>C=100 / M=0 / Y=100 / K=0</td>
          </tr>
          <tr>
            <td>Blue</td>
            <td>C=100 / M=0 / Y=0 / K=0</td>
          </tr>
        </table>
      </v-sheet>
    </v-card-text>

    <v-card-actions>
      <v-btn @click="view()" data-testid="tool_anaglyph_chart_view">{{ $t("common.view") }}</v-btn>
      <v-spacer />
      <v-btn color="primary" @click="print()" data-testid="tool_anaglyph_chart_print">{{ $t("common.print") }}</v-btn>
    </v-card-actions>
    <v-progress-linear v-if="in_flight" :indeterminate="true" />
  </v-card>
</template>

<script>
import printJS from "print-js";
import languages from "../../../../data/lang_codes.json";
import { Chance } from "chance";
let chance = Chance();

function alphabetHasCase(alphabet) {
  return Object.values(languages).some(
    (lang) => (lang.alphabet || "latin") === alphabet && !lang.single_case
  );
}

export default {
  components: {},
  props: {},
  data() {
    let alphabet = "latin";
    if (languages[this.$i18n.lang()] && languages[this.$i18n.lang()].alphabet) {
      alphabet = languages[this.$i18n.lang()].alphabet;
    }

    let data = {
      chars: "both",
      hue_strategy: "RandomRed",
      alphabet: alphabet,
      font: "OpticalSans",
      size: "large",
      fixation_lock: false,
      alphabet_options: [
        { text: this.$t("common.alphabet_latin"), value: "latin" },
        { text: this.$t("common.alphabet_arabic"), value: "arabic" },
        { text: this.$t("common.alphabet_greek"), value: "greek" },
        { text: this.$t("common.alphabet_hebrew"), value: "hebrew" }
      ],
      hue_strategy_options: [
        { text: this.$t("tool_anaglyph_cart.random_red"), value: "RandomRed" },
        {
          text: this.$t("tool_anaglyph_cart.random_green"),
          value: "RandomGreen",
        },
        {
          text: this.$t("tool_anaglyph_cart.random_blue"),
          value: "RandomBlue",
        },
        {
          text: this.$t("tool_anaglyph_cart.barred_red_green"),
          value: "StripedRedGreen",
        },
        {
          text: this.$t("tool_anaglyph_cart.barred_red_blue"),
          value: "StripedRedBlue",
        },
      ],
      size_options: [
        { text: this.$t("common.large"), value: "large" },
        { text: this.$t("common.medium_large"), value: "med_large" },
        { text: this.$t("common.medium_small"), value: "med_small" },
        { text: this.$t("common.small"), value: "small" },
      ],
      in_flight: false,
    };
    return data;
  },
  computed: {
    chars_options() {
      const hasCase = alphabetHasCase(this.alphabet);
      
      let options = [
        { text: this.$t("common.numbers_letters"), value: "both" },
        { text: this.$t("common.letters"), value: "letters" },
      ];
      
      // Only show "Numbers and lowercase letters" for alphabets that support lowercase
      if (hasCase) {
        options.push({ text: this.$t("tool_anaglyph_chart.numbers_and_lowercase_letters"), value: "lowerboth" });
      }
      
      options.push(
        { text: this.$t("common.numbers"), value: "numbers" },
        { text: this.$t("tool_anaglyph_cart.dice"), value: "dice" },
        {
          text: this.$t("tool_anaglyph_chart.directional_arrow"),
          value: "arrow",
        },
        {
          text: this.$t("tool_anaglyph_chart.directional_e"),
          value: "directionale",
        },
        { text: this.$t("tool_hart_chart.s_5"), value: "sfive" }
      );
      
      return options;
    },
    fonts_options() {
      if (this.alphabet == "latin" || this.chars == "numbers" || this.chars == "sfive") {
        return [
          { text: "Optical Sans", value: "OpticalSans" },
          { text: "Arial", value: "Arial" },
        ];
      } else {
        return [
          { text: "Optical Sans", value: "OpticalSans" },
        ];
      }
    },
    font_size() {
      if (
        this.hue_strategy == "RandomRed" ||
        this.hue_strategy == "RandomBlue" ||
        this.hue_strategy == "RandomGreen"
      ) {
        if (this.size == "large") {
          return 62;
        } else if (this.size == "med_large") {
          return 60;
        } else if (this.size == "med_small") {
          return 46;
        } else if (this.size == "small") {
          return 28;
        } else return "";
      } else {
        if (this.size == "large") {
          return 60;
        } else if (this.size == "med_large") {
          return 48;
        } else if (this.size == "med_small") {
          return 32;
        } else if (this.size == "small") {
          return 20;
        } else return "";
      }
    },
    rows() {
      return 8;
    },
    cols() {
      if (
        this.hue_strategy == "RandomRed" ||
        this.hue_strategy == "RandomBlue" ||
        this.hue_strategy == "RandomGreen"
      ) {
        return 6;
      } else {
        return 9;
      }
    },
  },
  watch: {
    chars() {
      // Optical Sans doesn't support lowercase characters
      if (this.chars == "lowerboth" && this.font == "OpticalSans") {
        this.font = "Arial";
      }
    },
    alphabet() {
      if (this.alphabet != "latin") {
        this.font = "OpticalSans";
      }
      // If current chars option is not available for new alphabet, reset to a valid option
      const hasCase = alphabetHasCase(this.alphabet);
      if (this.chars == "lowerboth" && !hasCase) {
        this.chars = "both";
      }
    }
  },
  methods: {
    view: function () {
      window.open(this.url(), "_blank");
    },
    url: function () {
      let fixation_lock = false;
      let hue_strategy = this.hue_strategy;
      if (
        hue_strategy == "RandomRed" ||
        hue_strategy == "RandomGreen" ||
        hue_strategy == "RandomBlue"
      ) {
        fixation_lock = this.fixation_lock;
      }

      let chars = this.chars;
      if (this.chars == "lowerboth") {
        // Handle "Numbers and lowercase letters" option
        if (this.alphabet == "greek") {
          chars = "lowergreekboth";
        } else {
          chars = "lowerboth";
        }
      } else if (this.alphabet != "latin" && (this.chars == "letters" || this.chars == "both")) {
        chars = this.alphabet + this.chars;
      }

      let uri = "/api/tools/pdf/anaglyph_chart.pdf";
      uri += "?chars=" + chars;
      uri += "&rows=" + this.rows;
      uri += "&cols=" + this.cols;
      uri += "&font_size=" + this.font_size;
      uri += "&hue_strategy=" + hue_strategy;
      uri += "&fixation_lock=" + fixation_lock;
      if (this.alphabet == "latin" && (this.chars == "letters" || this.chars == "both" || this.chars == "lowerboth")) {
        uri += "&font=" + encodeURIComponent(this.font);
      }
      uri += "&cache_buster=" + chance.hex();

      return uri;
    },
    print: function () {
      let self = this;

      if (navigator.vendor.includes("Apple")) {
        window.open(this.url(), "_blank");
        return;
      }

      printJS({
        printable: self.url(),
        onLoadingStart: function () {
          self.in_flight = true;
        },
        onLoadingEnd: function () {
          self.in_flight = false;
        },
      });
    },
    close: function () {
      this.$emit("close");
    },
  },
};
</script>


<style>
</style>